import { Context, Plugin } from '@nuxt/types'
import { isValid, parseISO } from 'date-fns'
import { castArray, isNil, transform } from 'lodash-es'
import { UnleashClient } from 'unleash-proxy-client'
import type { IVariant } from 'unleash-proxy-client'
import { flattenObject } from '~/lib/utils/lodash'

export enum Feature {
  ActivationPopup = 'activation-popup',
  DebugMode = 'debug-mode',
  FreemiumCredibility = 'freemium-credibility',
  FreemiumExitPopup = 'freemium-exit-popup',
  FreemiumLimited = 'freemium-limited',
  FreemiumProModal = 'freemium-pro-modal',
  FreemiumStatus = 'freemium-status',
  GoalRequirementScholarship = 'goal-requirement-scholarship',
  MLRecommenderVariant = 'ml-recommender-variant',
  NoEngagementSurvey = 'no-engagement-survey',
  SpiCheckboxes = 'spi-checkboxes',
  ActivationPopupPaid = 'activation-popup-paid',
  WinnersBtn = 'dashboard-header-winners-button',
  ExposedEligibilities = 'exposed-eligibilities',
  DashboardNewsTicker = 'dashboard-news-ticker',
  WinnerBlast = 'dashboard-winner-blast',
  PreviousWinners = 'dashboard-previous-winners',
  ProviderStatistics = 'dashboard-provider-stats',
  ReferralProgram = 'referral-program',
  NewDetailsPage = 'new-details-page',
  NewApplicationSuccessPage = 'new-application-success-page',
  NewMailboxPage = 'new-mailbox-page',
  DashboardCapBasedScholarships = 'dashboard-cap-based-scholarships',
  DashboardApplicationStrategyHealthBar = 'dashboard-application-strategy-health-bar',
  LoopGuestAccount = 'loop-guest-account',
  LoopTrialExtension = 'loop-trial-extension',
  PhoenixScholarshipLists = 'phoenix-scholarship-lists',
}

type ReportedFeature = {
  enabled: boolean
  variant?: string
}

type ReportedFeatures = Record<Feature, ReportedFeature>

export interface FeatureFlagService {
  getOverride: (feature: string) => string | boolean
  isEnabled: (feature: string) => boolean
  getVariant: (feature: string) => IVariant
  getAllForTracking: () => ReportedFeatures
  getAllForGTM: () => Record<string, string | boolean>
  isRolloutEligible: (feature: string) => boolean
}

declare module 'vue/types/vue' {
  interface Vue {
    $featureFlags: FeatureFlagService
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $featureFlags: FeatureFlagService
  }
  interface Context {
    $featureFlags: FeatureFlagService
  }
}

const featureFlags: Plugin = async(
  { $auth, $config, $sentry, route }: Context,
  inject
) => {
  try {
    if (!$auth.user) {
      return
    }

    const unleash = new UnleashClient($config['feature-flags'])

    await unleash.updateContext({ userId: $auth.user.id })

    await unleash.start()

    const featureFlags = {
      getOverride(feature: string) {
        const booleanValues = { true: true, false: false }
        const queryValue: string | null = castArray(route.query[feature])[0]
        const overrideValue: boolean | string =
          booleanValues[queryValue] ?? queryValue

        !isNil(overrideValue) &&
          // eslint-disable-next-line no-console
          console.info(
            `UNLEASH DEBUG: toggle "${feature}" is overriden by URL. Value used: "${overrideValue}"`
          )

        return overrideValue
      },
      isEnabled(feature: string) {
        return !isNil(this.getOverride(feature))
          ? this.getOverride(feature)
          : unleash.isEnabled(feature)
      },
      getVariant(feature: string) {
        return !isNil(this.getOverride(feature))
          ? { enabled: true, name: this.getOverride(feature) }
          : unleash.getVariant(feature)
      },
      getAllForTracking() {
        return transform(
          Feature,
          (result, feature) => {
            const variant = this.getVariant(feature)
            const enabled =
              variant.name === 'disabled'
                ? this.isEnabled(feature)
                : variant.enabled
            const reported: ReportedFeature = { enabled }

            if (variant.enabled) {
              reported.variant = variant.name
            }

            result[feature] = reported
          },
          {} as ReportedFeatures
        )
      },
      getAllForGTM() {
        return flattenObject(this.getAllForTracking())
      },
      isRolloutEligible(feature: string) {
        const variant = this.getVariant(feature)
        if (variant?.name !== 'test') { return false }
        const rolloutDate = variant?.payload?.value ? parseISO(variant.payload.value) : undefined
        if (!rolloutDate || !isValid(rolloutDate)) { return true }
        const signupDate = $auth.user?.createdAt ? parseISO($auth.user.createdAt) : undefined
        return (
          !!signupDate &&
          isValid(signupDate) &&
          signupDate > rolloutDate
        )
      }
    } as FeatureFlagService

    inject('featureFlags', featureFlags)
  } catch (err) {
    $sentry.captureException(err)
  }
}

export default featureFlags
